import { WithOwner } from "../IDBSchema";
import { EntityDataApi, IdbRowPayload } from "../IndexedDBApi";
import { ConstructionBase, ConstructionBasePatchPayload, ConstructionBasePutPayload } from "../../../schema/generated/Construction";
import { constructionRow, constructionRows } from "../../../schema/console/construction";
import { getFromIdPayload, setDeletePayload } from "../../../schema/general";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { qrScanProcessPayload } from "../../../schema/console/qrScan";
import Time from "../../../util/Time";
import { ConstructionForMaterialSearch } from "../../../schema/console/material";
import { MaterialBase } from "../../../schema/generated/Material";
import { idbEntityDefs } from "../generated/entityDefs";
import { FULL_EPS } from "../../../util/consumption";
import { required } from "../../../util/typed";

export class ConstructionDataApi extends EntityDataApi<ConstructionBase> {
  public readonly def = idbEntityDefs.Construction;

  public async getAll(ownerId: string): Promise<ConstructionBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<ConstructionBase | null> {
    return await this.findById(payload);
  }

  // 施工ごとの消費材料QR個票を消費記録(ConstructionMaterialConsumption)から解決する。
  // construction_id 直リンクではなく真ソースを使うため、部分消費・分割消費の材料も含む(APIと同方針)。
  private async consumedQrIdsByConstruction(ownerId: string): Promise<Map<string, Set<number>>> {
    const consumptions = await this.IDB().generated.constructionMaterialConsumption.getAllByOwner(ownerId);
    const byConstruction = new Map<string, Set<number>>();
    for (const consumption of consumptions) {
      if (consumption.deleted_at != null) {
        continue;
      }
      const set = byConstruction.get(consumption.construction_id) ?? new Set<number>();
      set.add(consumption.material_qr_id);
      byConstruction.set(consumption.construction_id, set);
    }
    return byConstruction;
  }

  public async getConstructionForMaterialSearch(payload: WithOwner<getFromIdPayload>): Promise<ConstructionForMaterialSearch | null> {
    const construction = await this.getById(payload);
    if (!construction) {
      return null;
    }
    const constructionPlan = await this.IDB().constructionPlanDataApi.getRowById({ id: construction.construction_plan_id, ownerId: payload.ownerId });
    if (!constructionPlan) {
      return null;
    }
    const constructionRecords = await this.IDB().constructionRecordDataApi.getByConstructionIdForMaterialSearch({ id: construction.id, ownerId: payload.ownerId });
    const user = await this.IDB().userDataApi.getFullDataById({ id: construction.preparer_user_id, ownerId: payload.ownerId });
    return { ...construction, User: user, ConstructionPlan: constructionPlan, ConstructionRecords: constructionRecords };
  }

  public async getRowById(payload: WithOwner<getFromIdPayload>): Promise<constructionRow | null> {
    const data = await this.getById(payload);
    if (!data) {
      return null;
    }
    const constructionPlan = await this.IDB().constructionPlanDataApi.getRowById({ id: data.construction_plan_id, ownerId: payload.ownerId });
    if (!constructionPlan) {
      return null;
    }
    const user = await this.IDB().userDataApi.getFullDataById({ id: data.preparer_user_id, ownerId: payload.ownerId });
    const materials = await this.IDB().materialDataApi.getByConstructionId({ id: data.id, ownerId: payload.ownerId });
    const constructionRecords = await this.IDB().constructionRecordDataApi.getByConstructionId({ id: data.id, ownerId: payload.ownerId });
    return {
      ...data,
      ConstructionPlan: constructionPlan,
      User: user,
      Materials: materials,
      ConstructionRecords: constructionRecords,
    };
  }

  public async getRowsByConstructionPlanId(payload: WithOwner<getFromIdPayload>): Promise<constructionRows> {
    // クロージャ内での型の絞り込みを効かせるためconstで受ける
    const [constructions, constructionPlan, materials, constructionRecords] = await Promise.all([
      this.readByIndex("construction_plan_id", payload.ownerId, payload.id),
      this.IDB().constructionPlanDataApi.getRowById(payload),
      this.IDB().materialDataApi.getAll(payload.ownerId),
      this.IDB().constructionRecordDataApi.getAll(payload.ownerId),
    ]);
    if (!constructionPlan) {
      return [];
    }
    const userIds = [...new Set([...constructions.map((c) => c.preparer_user_id), ...constructionRecords.map((r) => r.preparer_user_id)])];
    const users = await Promise.all(
      userIds.map((id) =>
        this.IDB().userDataApi.getFullDataById({ id, ownerId: payload.ownerId })
      )
    );
    const userMap = new Map(users.map((u) => [u.id, u]));
    const consumedQrIdsByConstruction = await this.consumedQrIdsByConstruction(payload.ownerId);
    const materialById = new Map(materials.map((m) => [m.id, m]));
    return constructions.map((construction) => ({
      ...construction,
      ConstructionPlan: constructionPlan,
      User: required(userMap.get(construction.preparer_user_id), "登録者ユーザー"),
      Materials: [...(consumedQrIdsByConstruction.get(construction.id) ?? [])].map((qrId) => materialById.get(qrId)).filter((m): m is MaterialBase => m != null),
      ConstructionRecords: constructionRecords.filter((record) => record.construction_id === construction.id).map((r) => ({ ...r, User: required(userMap.get(r.preparer_user_id), "登録者ユーザー") })),
    }));
  }

  public async put(data: WithOwner<ConstructionBasePutPayload>): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async patch(data: WithOwner<ConstructionBasePatchPayload>): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  // 施工の論理削除/復元。サーバの /set-delete-construction と同じコホート方式で子6テーブル
  // (ProcessExecution / StirBatch / ConstructionAction / QualityRecord /
  //  ConstructionMaterialConsumption / ConstructionRecord)へカスケードする。
  // 削除時は alive の子のみを親と同時刻で削除し、復元時は「親と同時に削除された子」のみ戻す
  // (全子行を無条件に戻すと、通常の子単独削除まで復活してしまう)。
  // ConstructionActionArea は construction_id を持たないため、対象施工の ConstructionAction 経由で
  // 対象化してから同じコホート判定を適用する。
  // 親行+子カスケード行はすべて1トランザクションで原子的に書き込む(途中失敗による中途半端な
  // 削除状態を防ぐ)。
  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    const parent = await this.findById({ id: data.id, ownerId: data.ownerId }, false);
    if (!parent) {
      throw new Error(`${this.def.store}に更新対象のデータがありません。`);
    }
    const previousDeletedAt = parent.deleted_at;

    if (data.deleted && previousDeletedAt != null) {
      // 既に削除済みの行への再削除は no-op(コホートの削除時刻を保つ)。
      return { status: "ok" };
    }
    if (!data.deleted && previousDeletedAt != null) {
      await this.assertRestorable(data.id, data.ownerId);
    }

    const now = Time.Now().GetTime();
    const deletedAt = data.deleted ? now : null;

    const constructionRow = { ...parent, deleted_at: deletedAt, updatedAt: now };

    const processExecutionApi = this.IDB().generated.processExecution;
    const stirBatchApi = this.IDB().generated.stirBatch;
    const constructionActionApi = this.IDB().generated.constructionAction;
    const qualityRecordApi = this.IDB().generated.qualityRecord;
    const cmcApi = this.IDB().generated.constructionMaterialConsumption;
    const constructionRecordApi = this.IDB().constructionRecordDataApi;
    const constructionActionAreaApi = this.IDB().generated.constructionActionArea;

    const byConstructionId = (row: { construction_id: string }) => row.construction_id === data.id;

    const processExecutionRows = await this.buildCascadeRows(processExecutionApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);
    const stirBatchRows = await this.buildCascadeRows(stirBatchApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);
    const constructionActionRows = await this.buildCascadeRows(constructionActionApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);
    const qualityRecordRows = await this.buildCascadeRows(qualityRecordApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);
    const cmcRows = await this.buildCascadeRows(cmcApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);
    const constructionRecordRows = await this.buildCascadeRows(constructionRecordApi, data.ownerId, byConstructionId, data.deleted, previousDeletedAt, deletedAt, now);

    // ConstructionActionArea は対象施工に属する ConstructionAction(削除済み含む全件)の id 経由で対象化する。
    const allActionsForConstruction = (await constructionActionApi.getAllByOwner(data.ownerId, false)).filter(byConstructionId);
    const actionIds = new Set(allActionsForConstruction.map((a) => a.id));
    const byActionIds = (row: { construction_action_id: string }) => actionIds.has(row.construction_action_id);
    const constructionActionAreaRows = await this.buildCascadeRows(constructionActionAreaApi, data.ownerId, byActionIds, data.deleted, previousDeletedAt, deletedAt, now);

    await this.IDB().putRowsAtomic([
      { store: this.def.store, rows: [constructionRow] },
      { store: processExecutionApi.def.store, rows: processExecutionRows },
      { store: stirBatchApi.def.store, rows: stirBatchRows },
      { store: constructionActionApi.def.store, rows: constructionActionRows },
      { store: qualityRecordApi.def.store, rows: qualityRecordRows },
      { store: cmcApi.def.store, rows: cmcRows },
      { store: constructionRecordApi.def.store, rows: constructionRecordRows },
      { store: constructionActionAreaApi.def.store, rows: constructionActionAreaRows },
    ]);
    return { status: "ok" };
  }

  // カスケード削除/復元対象の子行を、deleted_at を書き換えた行として返す。matchFn で対象行を絞り込む
  // (construction_id 直接一致、または ConstructionActionArea のような間接一致(親アクションID経由)の
  // どちらにも対応する)。
  // 削除時は alive の子のみを対象にする。
  // 復元時は「親と同時に削除された子」のみを戻す。まず deleted_at の ms 完全一致を試し、1件も無い場合
  // のみ秒 floor 一致にフォールバックする(サーバから再キャッシュされた親の deleted_at は datetime 列で
  // 秒精度に丸められるため)。ms 一致を優先することで、同一秒内に複数コホートの削除が起きていた場合に
  // 別コホートのトンボストーンまで誤って復活させるのを防ぐ。
  private async buildCascadeRows<TChild extends { id: string | number; deleted_at: number | null }>(
    childApi: EntityDataApi<TChild>,
    ownerId: string,
    matchFn: (row: TChild) => boolean,
    isDeleting: boolean,
    previousDeletedAt: number | null,
    deletedAt: number | null,
    now: number,
  ): Promise<IdbRowPayload[]> {
    const rows = await childApi.getAllByOwner(ownerId, false);
    const targetRows = rows.filter(matchFn);

    if (isDeleting) {
      const aliveRows = targetRows.filter((row) => row.deleted_at == null);
      return aliveRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
    }

    if (previousDeletedAt == null) {
      return [];
    }

    const exactMatchRows = targetRows.filter((row) => row.deleted_at === previousDeletedAt);
    if (exactMatchRows.length > 0) {
      return exactMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
    }

    const previousDeletedAtSecond = Math.floor(previousDeletedAt / 1000);
    const floorMatchRows = targetRows.filter((row) => row.deleted_at != null && Math.floor(row.deleted_at / 1000) === previousDeletedAtSecond);
    return floorMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
  }

  // QRスキャンによる施工処理。選択した材料QR個票を、この施工へ「まるごと消費(used_ratio=1 が既定)」として
  // ConstructionMaterialConsumption(施工材料消費、真ソース)へ記録する。スキャン後にリスト表示で材料ごと
  // に消費量(used_ratio)を patch で調整する運用のため、ここでは既定値 1 で消費行を作成する。撹拌バッチ・
  // 工程を伴わないまるごと消費のため stir_batch_id / process_execution_id は null。選択外になった材料の
  // 「まるごと消費」行は論理削除する。全量消費・残量は消費行の Σ used_ratio から都度計算される。
  public async process(payload: WithOwner<qrScanProcessPayload>): Promise<StatusResponse> {
    // API(material-scan-construction)と同じ事前検証: 施工の生存と Σ used_ratio ≤ 1 を
    // ローカル IndexedDB のデータで確認し、違反は書き込み前にエラーにする(復帰マージでの拒否を防ぐ)。
    // 施工行が IndexedDB に無い場合は同期不足の可能性があるため拒否しない(削除済みと確認できたときのみ拒否)。
    const construction = await this.findById({ id: payload.id, ownerId: payload.ownerId }, false);
    if (construction != null && construction.deleted_at != null) {
      throw new Error("スキャン対象の施工が削除済みです。画面を更新して最新の状態を確認してください。");
    }

    const cmcApi = this.IDB().generated.constructionMaterialConsumption;
    const existing = await cmcApi.getAllByOwner(payload.ownerId);
    const selected = new Set(payload.MaterialIDs);
    // この施工の「まるごと消費」行(撹拌バッチ・工程なし)の現状(alive のみ)
    const wholeConsumptions = existing.filter((c) => c.construction_id === payload.id && c.stir_batch_id == null && c.process_execution_id == null);
    const existingQrIds = new Set(wholeConsumptions.map((c) => c.material_qr_id));

    // 新規に「まるごと消費(used_ratio=1)」を作る材料は、既存の alive 消費との合計が1袋を超えないことを確認する
    for (const qrId of payload.MaterialIDs) {
      if (!existingQrIds.has(qrId)) {
        const totalWithWhole = existing.filter((c) => c.material_qr_id === qrId).reduce((sum, c) => sum + (c.used_ratio ?? 1), 0) + 1;
        if (totalWithWhole > 1 + FULL_EPS) {
          throw new Error(`材料QR ${qrId} の消費合計が1袋を超えます(合計 ${totalWithWhole.toFixed(3)})。画面を更新して残量を確認してください。`);
        }
      }
    }

    // 選択された材料で既存の「まるごと消費」行が無いものを used_ratio=1(既定)で新規作成
    for (const qrId of payload.MaterialIDs) {
      if (!existingQrIds.has(qrId)) {
        await cmcApi.offlinePut({ construction_id: payload.id, material_qr_id: qrId, stir_batch_id: null, process_execution_id: null, used_ratio: 1, ownerId: payload.ownerId });
      }
    }
    // 選択外になった材料の「まるごと消費」行を論理削除
    for (const consumption of wholeConsumptions) {
      if (!selected.has(consumption.material_qr_id)) {
        await cmcApi.offlineSetDelete({ id: consumption.id, ownerId: payload.ownerId, deleted: true });
      }
    }

    return { status: "ok" };
  }
}
